import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ExportBundle } from "@wodiary/shared";
import { api } from "../api/client.js";
import { useExportStatus } from "../api/queries.js";
import { errorMessage } from "../lib/errors.js";
import { formatDateTime } from "../lib/format.js";
import { downloadExportJson, downloadExportMarkdown } from "../lib/download.js";
import { NavBar } from "../components/NavBar.js";
import { Loading, QueryError } from "../components/AsyncState.js";
import { useToast } from "../components/ToastContext.js";
import { DownloadIcon, LockIcon } from "../components/icons.js";

/**
 * 导出（spec §5.7 `EXPORT-1`）。
 *
 * v1 **不做备份**（spec §8.3），所以这是唯一的数据出口 —— 设置页的「上次导出
 * 时间」是唯一能让「该导出了」被感知到的东西。
 *
 * 两种格式分工不同：Markdown 是不依赖本软件也能读的归档；JSON 是唯一可用于
 * 重建的结构化副本。
 */
export function ExportPage() {
  const status = useExportStatus();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [bundle, setBundle] = useState<ExportBundle | null>(null);

  const run = useMutation({
    mutationFn: api.runExport,
    onSuccess: (data) => {
      setBundle(data);
      void queryClient.invalidateQueries({ queryKey: ["export-status"] });
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
      downloadExportJson(data);
      toast.show(`已导出 ${data.entries.length} 篇日记`);
    },
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <>
      <NavBar title="导出" back />
      <div className="page">
        {status.isLoading && <Loading />}
        {status.isError && (
          <QueryError error={status.error} onRetry={() => void status.refetch()} />
        )}

        {error !== null && <div className="banner banner-error">{error}</div>}

        <div className="banner banner-warn">
          <LockIcon size={16} />
          <span>
            导出范围是「我视角下的全部」：我写的全部（含私密）+ TA 共享给我的。
            TA 写给自己看的东西不在里面 —— 本来就看不到。
          </span>
        </div>

        <section className="card stack-sm">
          <div className="section-title">上次导出</div>
          <div className="muted small">
            {status.data?.last_exported_at != null
              ? formatDateTime(status.data.last_exported_at)
              : "还没有导出过"}
          </div>
        </section>

        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={run.isPending}
          onClick={() => {
            setError(null);
            run.mutate();
          }}
        >
          <DownloadIcon size={17} />
          {run.isPending ? "正在打包…" : "导出并下载 JSON"}
        </button>

        {bundle !== null && (
          <>
            <div className="banner banner-info">
              已生成 {bundle.entries.length} 篇日记的归档。
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-block"
              onClick={() => downloadExportMarkdown(bundle)}
            >
              再下载一份 Markdown（可读归档）
            </button>
          </>
        )}

        <p className="foot-note">
          Markdown 能读不能恢复（附件、时间戳、可见性等结构信息会在排版里丢失）；
          JSON 是唯一可用于重建的结构化副本。附件存的是稳定的对象名，不是会过期的
          签名链接。
        </p>
      </div>
    </>
  );
}
